import React, { useState } from 'react';
import { Heart, Home, BookOpen, Save, MessageSquare, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface Verse {
  text: string;
  reference: string;
  emotion: string;
}

// Banco de versículos organizados por emoção
const versesDatabase: Record<string, Verse[]> = {
  ansiedade: [
    { 
      text: "Não andem ansiosos por coisa alguma, mas em tudo, pela oração e súplicas, e com ação de graças, apresentem seus pedidos a Deus. E a paz de Deus, que excede todo o entendimento, guardará o coração e a mente de vocês em Cristo Jesus.", 
      reference: "Filipenses 4:6-7",
      emotion: "ansiedade"
    },
    { 
      text: "Lancem sobre ele toda a sua ansiedade, porque ele tem cuidado de vocês.", 
      reference: "1 Pedro 5:7",
      emotion: "ansiedade"
    },
    { 
      text: "Quando a ansiedade já me dominava no íntimo, o teu consolo trouxe alívio à minha alma.", 
      reference: "Salmos 94:19",
      emotion: "ansiedade"
    }
  ],
  tristeza: [
    { 
      text: "O Senhor está perto dos que têm o coração quebrantado e salva os de espírito abatido.", 
      reference: "Salmos 34:18",
      emotion: "tristeza"
    },
    { 
      text: "Bem-aventurados os que choram, pois serão consolados.", 
      reference: "Mateus 5:4",
      emotion: "tristeza"
    },
    { 
      text: "Ele enxugará dos seus olhos toda lágrima. Não haverá mais morte, nem tristeza, nem choro, nem dor, pois a antiga ordem já passou.", 
      reference: "Apocalipse 21:4",
      emotion: "tristeza"
    }
  ],
  gratidão: [
    { 
      text: "Deem graças em todas as circunstâncias, pois esta é a vontade de Deus para vocês em Cristo Jesus.", 
      reference: "1 Tessalonicenses 5:18",
      emotion: "gratidão"
    },
    { 
      text: "Entrem por suas portas com ações de graças e em seus átrios com louvor; deem-lhe graças e bendigam o seu nome.", 
      reference: "Salmos 100:4",
      emotion: "gratidão"
    },
    { 
      text: "Não andem ansiosos por coisa alguma, mas em tudo, pela oração e súplicas, e com ação de graças, apresentem seus pedidos a Deus.", 
      reference: "Filipenses 4:6",
      emotion: "gratidão"
    }
  ],
  medo: [
    { 
      text: "Pois Deus não nos deu espírito de covardia, mas de poder, de amor e de equilíbrio.", 
      reference: "2 Timóteo 1:7",
      emotion: "medo"
    },
    { 
      text: "Não temas, porque eu sou contigo; não te assombres, porque eu sou teu Deus; eu te fortaleço, e te ajudo, e te sustento com a destra da minha justiça.", 
      reference: "Isaías 41:10",
      emotion: "medo"
    },
    { 
      text: "No amor não há medo; antes, o perfeito amor lança fora o medo. Ora, o medo produz tormento; logo, aquele que teme não é perfeito no amor.", 
      reference: "1 João 4:18",
      emotion: "medo"
    }
  ],
  alegria: [
    { 
      text: "Alegrem-se sempre no Senhor. Novamente direi: alegrem-se!", 
      reference: "Filipenses 4:4",
      emotion: "alegria"
    },
    { 
      text: "Este é o dia em que o Senhor agiu; alegremo-nos e exultemos neste dia.", 
      reference: "Salmos 118:24",
      emotion: "alegria"
    },
    { 
      text: "Pois a alegria do Senhor é a sua força.", 
      reference: "Neemias 8:10",
      emotion: "alegria"
    }
  ],
  raiva: [
    { 
      text: "Irem-se, e não pequem; não se ponha o sol sobre a vossa ira.", 
      reference: "Efésios 4:26",
      emotion: "raiva"
    },
    { 
      text: "Sabei isto, meus amados irmãos: Todo homem seja pronto para ouvir, tardio para falar e tardio para se irar. Porque a ira do homem não produz a justiça de Deus.", 
      reference: "Tiago 1:19-20",
      emotion: "raiva"
    },
    { 
      text: "Deixe a ira, abandone o furor; não se irrite, pois isso só leva ao mal.", 
      reference: "Salmos 37:8",
      emotion: "raiva"
    }
  ],
  solidão: [
    { 
      text: "Não te deixarei, nem te desampararei.", 
      reference: "Hebreus 13:5",
      emotion: "solidão"
    },
    { 
      text: "Mesmo quando eu andar por um vale de trevas e morte, não temerei perigo algum, pois tu estás comigo; a tua vara e o teu cajado me protegem.", 
      reference: "Salmos 23:4",
      emotion: "solidão"
    },
    { 
      text: "Ensina-me o teu caminho, Senhor, para que eu ande na tua verdade; dá-me um coração inteiramente fiel, para que eu tema o teu nome.", 
      reference: "Salmos 86:11",
      emotion: "solidão"
    }
  ],
  esperança: [
    { 
      text: "Porque sou eu que conheço os planos que tenho para vocês, diz o Senhor, planos de fazê-los prosperar e não de causar dano, planos de dar a vocês esperança e um futuro.", 
      reference: "Jeremias 29:11",
      emotion: "esperança"
    },
    { 
      text: "Espere no Senhor. Seja forte! Coragem! Espere no Senhor.", 
      reference: "Salmos 27:14",
      emotion: "esperança"
    },
    { 
      text: "Mas os que esperam no Senhor renovam as suas forças. Voam alto como águias; correm e não ficam exaustos, andam e não se cansam.", 
      reference: "Isaías 40:31",
      emotion: "esperança"
    }
  ],
  desânimo: [
    { 
      text: "Vinde a mim, todos os que estais cansados e sobrecarregados, e eu vos aliviarei.", 
      reference: "Mateus 11:28",
      emotion: "desânimo"
    },
    { 
      text: "Não se turbe o vosso coração; credes em Deus, crede também em mim.", 
      reference: "João 14:1",
      emotion: "desânimo"
    },
    { 
      text: "Posso todas as coisas naquele que me fortalece.", 
      reference: "Filipenses 4:13",
      emotion: "desânimo"
    }
  ],
  paz: [
    { 
      text: "Deixo-vos a paz, a minha paz vos dou; não vo-la dou como o mundo a dá. Não se turbe o vosso coração, nem se atemorize.", 
      reference: "João 14:27",
      emotion: "paz"
    },
    { 
      text: "E a paz de Deus, que excede todo o entendimento, guardará os vossos corações e os vossos pensamentos em Cristo Jesus.", 
      reference: "Filipenses 4:7",
      emotion: "paz"
    },
    { 
      text: "Grande paz têm os que amam a tua lei; para eles não há tropeço.", 
      reference: "Salmos 119:165",
      emotion: "paz"
    }
  ]
};

// Lista de emoções disponíveis
const availableEmotions = [
  { id: 'ansiedade', label: 'Ansiedade', icon: '😰' },
  { id: 'tristeza', label: 'Tristeza', icon: '😢' },
  { id: 'gratidão', label: 'Gratidão', icon: '🙏' },
  { id: 'medo', label: 'Medo', icon: '😨' },
  { id: 'alegria', label: 'Alegria', icon: '😊' },
  { id: 'raiva', label: 'Raiva', icon: '😠' },
  { id: 'solidão', label: 'Solidão', icon: '🥺' },
  { id: 'esperança', label: 'Esperança', icon: '✨' },
  { id: 'desânimo', label: 'Desânimo', icon: '😔' },
  { id: 'paz', label: 'Paz', icon: '😌' }
];

export default function SpiritualCounselor() {
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentVerse, setCurrentVerse] = useState<Verse | null>(null);
  const [savedVerses, setSavedVerses] = useState<Verse[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSavedVerses, setShowSavedVerses] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const navigate = useNavigate();

  // Filtrar emoções com base na busca
  const filteredEmotions = availableEmotions.filter(emotion => 
    emotion.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Selecionar uma emoção e obter um versículo
  const selectEmotion = (emotionId: string) => {
    setSelectedEmotion(emotionId);
    
    // Obter versículos para a emoção selecionada
    const versesForEmotion = versesDatabase[emotionId] || [];
    
    if (versesForEmotion.length > 0) {
      // Selecionar um versículo aleatório
      const randomIndex = Math.floor(Math.random() * versesForEmotion.length);
      setCurrentVerse(versesForEmotion[randomIndex]);
    } else {
      setCurrentVerse(null);
    }
  };

  // Obter outro versículo para a mesma emoção
  const getAnotherVerse = () => {
    if (!selectedEmotion) return;
    
    const versesForEmotion = versesDatabase[selectedEmotion] || [];
    
    if (versesForEmotion.length > 0) {
      // Filtrar para não repetir o versículo atual
      const availableVerses = currentVerse 
        ? versesForEmotion.filter(verse => verse.reference !== currentVerse.reference)
        : versesForEmotion;
      
      if (availableVerses.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableVerses.length);
        setCurrentVerse(availableVerses[randomIndex]);
      } else {
        // Se não houver outros versículos disponíveis, usar qualquer um
        const randomIndex = Math.floor(Math.random() * versesForEmotion.length);
        setCurrentVerse(versesForEmotion[randomIndex]);
      }
    }
  };

  // Salvar versículo
  const saveVerse = async () => {
    if (!currentVerse) return;
    
    setLoading(true);
    
    try {
      // Verificar se o usuário está logado
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Se não estiver logado, salvar localmente
        const localSavedVerses = JSON.parse(localStorage.getItem('savedVerses') || '[]');
        localSavedVerses.push(currentVerse);
        localStorage.setItem('savedVerses', JSON.stringify(localSavedVerses));
      } else {
        // Se estiver logado, salvar no Supabase (simulado aqui)
        // Em uma implementação real, você salvaria no banco de dados
        const localSavedVerses = JSON.parse(localStorage.getItem('savedVerses') || '[]');
        localSavedVerses.push(currentVerse);
        localStorage.setItem('savedVerses', JSON.stringify(localSavedVerses));
      }
      
      // Atualizar a lista de versículos salvos
      const updatedSavedVerses = JSON.parse(localStorage.getItem('savedVerses') || '[]');
      setSavedVerses(updatedSavedVerses);
      
      // Mostrar mensagem de sucesso
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Erro ao salvar versículo:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar versículos salvos
  React.useEffect(() => {
    const loadSavedVerses = async () => {
      try {
        // Carregar versículos salvos do localStorage
        const localSavedVerses = JSON.parse(localStorage.getItem('savedVerses') || '[]');
        setSavedVerses(localSavedVerses);
      } catch (error) {
        console.error('Erro ao carregar versículos salvos:', error);
      }
    };
    
    loadSavedVerses();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Heart className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Conselheiro Espiritual</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Coluna da esquerda - Seleção de emoção */}
        <div className="md:col-span-1">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-xl font-semibold text-brand-teal mb-4">Como você está se sentindo?</h2>
            <p className="text-gray-600 mb-4">
              Selecione uma emoção para receber um versículo bíblico personalizado que trará conforto, orientação e esperança.
            </p>
            
            {/* Barra de busca */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar emoção..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              />
            </div>
            
            {/* Lista de emoções */}
            <div className="space-y-2">
              {filteredEmotions.map((emotion) => (
                <button
                  key={emotion.id}
                  onClick={() => selectEmotion(emotion.id)}
                  className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                    selectedEmotion === emotion.id
                      ? 'bg-brand-light text-brand-teal'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="text-2xl">{emotion.icon}</span>
                  <span className="font-medium">{emotion.label}</span>
                </button>
              ))}
            </div>
            
            {/* Botão para ver versículos salvos */}
            <button
              onClick={() => setShowSavedVerses(!showSavedVerses)}
              className="w-full mt-6 px-4 py-2 border border-brand-teal text-brand-teal rounded-lg hover:bg-brand-light transition-colors flex items-center justify-center gap-2"
            >
              <BookOpen className="h-4 w-4" />
              {showSavedVerses ? 'Ocultar versículos salvos' : 'Ver versículos salvos'}
            </button>
          </div>
        </div>
        
        {/* Coluna da direita - Versículo ou versículos salvos */}
        <div className="md:col-span-2">
          {showSavedVerses ? (
            // Mostrar versículos salvos
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-semibold text-brand-teal mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Versículos Salvos
              </h2>
              
              {savedVerses.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Você ainda não salvou nenhum versículo.
                  </p>
                  <button
                    onClick={() => setShowSavedVerses(false)}
                    className="mt-4 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                  >
                    Encontrar versículos
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {savedVerses.map((verse, index) => (
                    <div key={index} className="p-4 bg-brand-light rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm px-2 py-1 bg-white text-brand-teal rounded-full">
                          {availableEmotions.find(e => e.id === verse.emotion)?.label || verse.emotion}
                        </span>
                      </div>
                      <p className="text-gray-700 italic mb-2">"{verse.text}"</p>
                      <p className="text-right text-brand-teal font-medium">{verse.reference}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Mostrar versículo atual
            <div className="bg-white p-6 rounded-xl shadow-sm">
              {currentVerse ? (
                <>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm px-2 py-1 bg-brand-light text-brand-teal rounded-full flex items-center gap-1">
                        {availableEmotions.find(e => e.id === selectedEmotion)?.icon}
                        <span>{availableEmotions.find(e => e.id === selectedEmotion)?.label}</span>
                      </span>
                    </div>
                    
                    <div className="p-6 bg-brand-light rounded-lg mb-4">
                      <p className="text-gray-700 text-lg italic mb-4">"{currentVerse.text}"</p>
                      <p className="text-right text-brand-teal font-semibold">{currentVerse.reference}</p>
                    </div>
                    
                    <p className="text-gray-600">
                      Este versículo foi escolhido especialmente para momentos de {availableEmotions.find(e => e.id === selectedEmotion)?.label.toLowerCase()}. 
                      Que estas palavras tragam conforto e orientação para o seu coração.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={getAnotherVerse}
                      className="flex-1 px-4 py-2 border border-brand-teal text-brand-teal rounded-lg hover:bg-brand-light transition-colors"
                    >
                      Outro versículo
                    </button>
                    
                    <button
                      onClick={saveVerse}
                      disabled={loading}
                      className="flex-1 bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <span>Salvando...</span>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          <span>Salvar este versículo</span>
                        </>
                      )}
                    </button>
                  </div>
                  
                  {saveSuccess && (
                    <div className="mt-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm">
                      Versículo salvo com sucesso!
                    </div>
                  )}
                </>
              ) : selectedEmotion ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Não encontramos versículos para esta emoção.
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-700 mb-2">
                    Bem-vindo ao Conselheiro Espiritual
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-6">
                    Selecione como você está se sentindo no menu ao lado para receber um versículo bíblico personalizado que trará conforto e orientação.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {availableEmotions.slice(0, 5).map((emotion) => (
                      <button
                        key={emotion.id}
                        onClick={() => selectEmotion(emotion.id)}
                        className="px-3 py-1 bg-brand-light text-brand-teal rounded-full flex items-center gap-1 hover:bg-brand-light/80 transition-colors"
                      >
                        <span>{emotion.icon}</span>
                        <span>{emotion.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}